import { ChevronLeft } from 'lucide-react'
import { ReactNode } from 'react'
import { Link } from 'react-router'

import { PageHeader } from '@/components/ds'
import { useSetPageHeader } from '@/contexts/PageHeaderContext'
import { cn } from '@/lib/utils'

const widthClass = {
  narrow: 'max-w-2xl',
  list: 'max-w-4xl',
  wide: 'max-w-[1280px]',
} as const

export interface SettingsPageProps {
  title: string
  description?: ReactNode
  actions?: ReactNode
  /** Kam vede odkaz zpět, výchozí je přehled nastavení. */
  back?: { href: string; label: string } | null
  width?: keyof typeof widthClass
  breadcrumbHref?: string
  children: ReactNode
}

/** Kostra stránky nastavení: drobečky, odkaz zpět, nadpis a úzký sloupec obsahu. */
export const SettingsPage = ({
  title,
  description,
  actions,
  back = { href: '/settings', label: 'Nastavení' },
  width = 'narrow',
  breadcrumbHref,
  children,
}: SettingsPageProps) => {
  useSetPageHeader({
    breadcrumbs: [
      { href: '/settings', children: 'Nastavení' },
      ...(breadcrumbHref ? [{ href: breadcrumbHref, children: title }] : []),
    ],
    newButton: 'invoice',
  })

  return (
    <>
      <title>{`${title} | Nastavení`}</title>
      <div className={cn('mx-auto w-full', widthClass[width])}>
        {back && (
          <Link
            to={back.href}
            className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <ChevronLeft className="size-4" />
            {back.label}
          </Link>
        )}
        <PageHeader title={title} description={description} actions={actions} />
        <div>{children}</div>
      </div>
    </>
  )
}

export interface SettingsSectionProps {
  title?: ReactNode
  description?: ReactNode
  actions?: ReactNode
  children?: ReactNode
  className?: string
}

/** Sekce formuláře: nadpis, pomocný text a obsah. Sekce se oddělují linkou. */
export const SettingsSection = ({ title, description, actions, children, className }: SettingsSectionProps) => (
  <section className={cn('border-t py-6 first:border-t-0 first:pt-0', className)}>
    {(title || description || actions) && (
      <div className="mb-4 flex items-start justify-between gap-4">
        <div className="min-w-0">
          {title && <h2 className="text-sm font-medium">{title}</h2>}
          {description && <p className="mt-1 text-sm text-pretty text-muted-foreground">{description}</p>}
        </div>
        {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </div>
    )}
    {children}
  </section>
)

/** Patička formuláře s tlačítkem Uložit. */
export const SettingsFormFooter = ({ children, hint, className }: { children: ReactNode; hint?: ReactNode; className?: string }) => (
  <div className={cn('mt-6 flex items-center justify-between gap-4 border-t pt-4', className)}>
    <p className="text-xs text-muted-foreground">{hint}</p>
    <div className="flex items-center gap-2">{children}</div>
  </div>
)

/** Řádek volby v ohraničeném seznamu (přepínače, radio volby). */
export const SettingsOptionList = ({ children, className }: { children: ReactNode; className?: string }) => (
  <div className={cn('divide-y overflow-hidden rounded-lg border', className)}>{children}</div>
)
